<template>
  <div class="w-full" :key="order.updated_at">
    <portal to="search">
      <div class="flex items-center">
        <h1 class="font-bold text-lg md:text-2xl">
          <inertia-link
            class="text-indigo-400 hover:text-indigo-600"
            :href="route('customer.orders')"
            >
            Customer Orders
          </inertia-link>
          <span class="text-indigo-400 font-medium">/</span>
          Order #{{ order.id }}
        </h1>
      </div>
    </portal>

    <div class="grid grid-cols-1 xl:grid-cols-12 gap-6">
      <div class="col-span-full xl:col-span-10">
        <div>
          <OrderTabs
            active="order"
            :order-id="order.id"
          />
          <OrderTabContent>
            <div class="px-4 py-5 sm:px-6 divide-y divide-gray-200 space-y-5">
              <div v-if="!order.customer_handover_date">
                <div class="rounded-md bg-yellow-50 border border-yellow-300 p-4">
                  <div class="flex">
                    <div class="shrink-0 size-5 text-yellow-400">
                      <Icon name="exclamation-triangle" class="h-6 w-6" />
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-yellow-800">Attention needed</h3>
                      <div class="mt-2 text-sm text-yellow-700">
                        <p>The delivery or handover date is missing. Please set the date to avoid delays or processing restrictions. <br /><br />
                          click <InertiaLink :href="route('customer.orders.fulfilment-details', {
                            customerOrder: order,
                            subAction: 'edit-details'
                          })" class="text-blue-500 font-bold">Fulfilment Details</InertiaLink> to update
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="">
                <div class="flex justify-between items-center">
                  <div>
                    <a :href="route('customers.edit', order.customer_account.id)" target="_blank">
                      <h3 class="text-lg leading-6 font-medium text-gray-900">
                        {{ order.customer_account.name }}
                      </h3>
                    </a>
                  </div>

                  <span>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-pink-100 text-pink-800" v-if="order.customer_account.credit_customer">CREDIT CUSTOMER</span>
                    <span class="text-sm leading-5 text-gray-500">Status: </span>
                    <span class="uppercase text-sm leading-5 text-gray-900">{{ order.status_label }}</span>
                  </span>
                </div>

                <p class="flex justify-between mt-1 text-sm leading-5 text-gray-500">
                  <span v-if="order.customer_account.personal_account">PERSONAL ACCOUNT</span>
                  <span v-if="order.customer_account.tin_number">TIN: {{ order.customer_account.tin_number }}</span>
                  <span>Date: {{ order.created_at | localDateTime }}</span>
                </p>
              </div>

              <div class="space-y-3 pt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Customer Reference
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-lg">
                        <div>
                          <template v-if="order.customer_reference">
                            {{ order.customer_reference }}
                          </template>
                          <text-input v-else :disabled="isLocked" :errors="$page.props.errors.customer_contact_number" v-model="form.customer_reference" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Created By
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-lg">
                        <div class="flex justify-between items-center">
                          <div>
                            <template v-if="order.created_by">
                              <div class="text-xs text-gray-500 mb-2">{{ order.created_by.customer ? 'Customer' : 'Staff' }}</div>
                              <div>{{ `${order.created_by.first_name} ${order.created_by.last_name}` }}</div>
                            </template>
                            <template v-else>
                              <div class="text-xs text-gray-500 mb-2">Deleted User</div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="order.quotation_ref" class="space-y-3 pt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Quotation
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-lg">
                        <div class="flex justify-between items-center">
                          <div>
                            <div class="text-xs text-gray-500 mb-2">{{ order.quotation_ref }}</div>
                            <div>{{ order.quotation_amount }}</div>
                            <div class="my-4" >
                              <a v-if="order.quotation_file_path" :href="route('customer.orders.download-file', {customerOrder: order.id, file: 'quotation', preview: 'preview' })" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a>
                              <inertia-link v-if="canAdmin('sync-quotation') && order.quotation_ref" :href="route('customer.orders.sync-quotation', order.id)" class="btn-primary px-2.5 py-1.5 ml-2">Sync Quotation</inertia-link>
                            </div>

                            <div class="mt-2 text-sm">{{ order.quotation_customer_approved === true ? 'Customer has accepted quotation' : 'Customer has not accepted the quotation yet.' }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="order.invoice_ref" class="space-y-3 pt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Invoice
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-lg">
                        <div class="flex justify-between items-center">
                          <div>
                            <div class="text-xs text-gray-500 mb-2">{{ order.invoice_ref }}</div>
                            <div>{{ order.invoice_amount }}</div>
                            <div class="my-4"><a :href="route('customer.orders.download-file', {customerOrder: order.id, file: 'invoice',  preview: 'preview'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="order.status === 'review' && order.review_data.length > 0" class="space-y-3 pt-4">
                <h3 class="text-lg leading-5">REVIEW STATUS</h3>
                <div class="divide-y divide-yellow-600 divide-dashed">
                  <div v-for="(review, indx) in order.review_data" :key="indx" class="py-2">
                    <h4 class="text-sm">{{ review.message }}</h4>
                    <div v-if="review.type !== 'message_only'">

                      <div v-if="review.type === 'item_qty_mismatch'" class="text-yellow-700 text-xs pl-2 my-1">
                        <ul v-for="(issues, itemCode) in review.item_codes">
                          <li><b>{{ itemCode }}</b>
                            <span v-for="issue in issues" :key="issue.unit + itemCode" class="mx-3">
                          {{ issue.unit}} : Qty {{ issue.qty }}
                        </span>
                          </li>
                        </ul>
                      </div>

                      <div v-if="review.type === 'not_in_order' || review.type === 'not_in_invoice'" class="text-yellow-700 text-xs pl-2 my-1">
                        <div class="grid divide-x divide-yellow-600 grid-cols-12">
                          <div v-for="itemCode in review.item_codes" :key="itemCode"> <b>{{ itemCode }}</b></div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <div class="space-y-3 pt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Notification Contact Number
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-lg sm:max-w-xs flex items-center gap-2">
                        <text-input :disabled="isLocked" :errors="$page.props.errors.customer_contact_number" v-model="form.customer_contact_number" />
                        <a v-if="form.customer_contact_number" :href="`tel:${form.customer_contact_number}`" class="text-sm leading-5 text-gray-500 hover:text-gray-900 p-3">
                          <Icon name="phone" class="h-4 w-4 mx-1" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="space-y-3 pt-4">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Remarks
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-lg">
                        <textarea-input :disabled="isLocked" v-model="form.remarks" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="space-y-3 pt-4">
                <OrderItems
                  v-model="form.items"
                  :duplicate-items="duplicateItems"
                  :can_update_order_items="can_update_order_items"
                  :is-locked="isLocked"
                  :update-item-price="updateItemPrice"
                  :get-warehouse-list-for="getWarehouseListFor"
                  :can-change-item-location="canChangeItemLocation"
                  :total_price="total_price"
                  :last-updated="order.updated_at"
                  :order-id="order.id"
                  :order-status="order.status"
                  :orderIsCompleted="orderIsCompleted"
                  :processing="form.processing"
                  :warehouse-list="warehouseList"
                  :approved-at="order.approved_at"
                  @updateOrder="updateOrder"
                  @approveOrder="approveOrder"
                />
              </div>
            </div>
          </OrderTabContent>
        </div>
      </div>

      <div class="col-span-full xl:col-span-2">
        <div class="grid grid-cols-1 gap-y-2 sticky top-6">
          <button class="btn-primary" v-if="canAdmin('confirm-quotation') && order.approved_at &&  order.status === 'approved' && order.quotation_file_path" @click="confirmQuotation">Confirm Quotation</button>
          <button class="btn-primary" v-if="canAdmin('create-quotation') && order.status === 'approved' && !order.quotation_ref" @click="createQuotation">Create Quotation</button>
          <button class="btn-primary" v-if="canAdmin('create-quotation') && order.quotation_ref" @click="createQuotation">Regenerate Quotation</button>
          <button class="btn-primary" v-if="canAdmin('create-quotation') && order.quotation_ref && !order.invoice_ref" @click="createCreditInvoice">Make Credit Invoice</button>
          <button class="btn-primary"
                  :disabled="(!order.approved_at && !enableQuoatingWhenNotApproved) || order.status === 'completed' || orderIsCompleted === true"
                  @click="modals.invoice = true">Update Invoice</button>
          <a class="btn-primary text-center" :href="route('customer.orders.invoice-sync', {customerOrder: order.id})">Sync Invoice</a>
          <a class="btn-primary text-center" :href="route('customer.orders.csv', {customerOrder: order.id})">Download CSV</a>

          <a v-if="order.status === 'review'" class="btn-indigo text-center" :href="route('customer.orders.force-to-invoiced', {customerOrder: order.id})">Force Update to Invoiced</a>
          <a v-if="order.status === 'review'" class="bg-gray-700 rounded btn-gray text-center" :href="route('customer.orders.force-to-quoted', {customerOrder: order.id})">Force Revert to Quoted</a>

          <loading-button :loading="unlocking" v-if="order.lock && order.status !== 'completed'" class="btn-primary justify-center" @click="unlockOrder">Unlock</loading-button>
          <button :disabled="order.status === 'invoiced' || order.status === 'completed'" class="btn-red" @click="modals.cancel = true">Cancel Order</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from "@/Shared/Admin/Layout";
import LoadingButton from '@/Shared/LoadingButton'
import OrderTabs from "./Partials/OrderTabs.vue";
import OrderTabContent from "./Partials/OrderTabContent.vue";
import TextInput from "../../../Shared/TextInput.vue";
import Icon from "../../../Shared/Icon.vue";
import OrderItems from "./Partials/OrderItems.vue";
import TextareaInput from "../../../Shared/TextareaInput.vue";
import {mapFormValue} from "../../../Utils";

export default {
  name: 'OrderDetails',
  layout: Layout,
  metaInfo: { title: 'Customer Orders' },
  props: {
    order: Object,
    order_total_price: Number,
    warehouses: Object,
    teams: Array,
    isCredit: Boolean,
    locations: Object,
    customerAccountLocation: String,
    vesselLocations: Object,
    enableQuoatingWhenNotApproved: Boolean,
    paymentMethods: Array,
    warehouseList: Array,
    isSuperAdmin: Boolean,
    duplicateItems: [Object, Array],
    orderIsCompleted: Boolean,
    user_warehouses: [Array],
    cancellationOptions: [Object],
  },

  components: {
    TextareaInput,
    OrderItems,
    Icon,
    TextInput,
    OrderTabContent,
    OrderTabs,
    LoadingButton,
  },
  data() {
    return {
      handover_wcode: null,
      total_price: this.order_total_price,
      // items available quantity details
      available_qty: {
        loaded: false,
        items: {}
      },
      viewing_handover_list_id: null,
      can_update_order_items: false,
      can_update_order_item_location: false,
      form: this.$inertia.form({
        customer_to_collect: null,
        delivery_address: null,
        delivery_location: null,
        customer_handover_date: null,
        vessel: null,
        vessel_location: null,
        boat_id: null,
        delivery_contact_number: null,
        customer_contact_number: null,
        customer_reference: null,
        remarks: null,
        items: null,
      }),

      cancelOrderForm: {
        reason_type: null,
        reason_details: null
      },

      updateItems: [],

      require_payment_slip: !this.order.customer_payment_transfer_slip_image_path,
      payment_received_at: this.order.payment_received_at,
      payment_transfer_at: this.order.customer_payment_transfer_at,
      payment_slip: null,
      payment_method: this.order.payment_method ? this.order.payment_method : 'transfer', // transfer is set as default payment method

      checkedItems: {},

      quote: {
        quotation_ref: this.order.quotation_ref,
        quotation_amount: this.order.quotation_amount,
        quotation_file: null,
        advance_payment: this.order.advance_payment,
      },

      invoice: {
        quotation_customer_approved: this.order.quotation_customer_approved,
        invoice_ref: this.order.invoice_ref,
        invoice_amount: this.order.invoice_amount,
        invoice_file: null,
        lock: true
      },

      modals: {
        quotation: false,
        invoice: false,
        cancel: false,
        addItem: false,
        removeItem: false,
        updateSelectedItems: false,
      },

      unlocking: false,
      orderUpdating: false,
      updatingQuotation: false,
      updatingInvoice: false,
      paymentUpdating: false,
      cancelling: false,
      removingItems: false
    }
  },

  computed: {
    isLocked() {
      if (this.canAdmin('update-orders')) {
        if (!this.order.customer_handover_date) {
          return true
        }

        return this.order.lock;
      }
      return true;
    },
    canChangeItemLocation(item) {
      return function () {
        return this.canAdmin('update-order-item-location') && item.completed_at !== null;
      }
    },
    isItemLocked() {
      return function (item) {
        return item.completed_at !== null || item.handovers.length > 0;
      }
    },
  },

  methods: {
    confirmQuotation() {
      this.orderUpdating = true;
      this.$inertia.post(this.route('customer.orders.confirm-quotation'), {
        orderId: this.order.id
      },{
        onSuccess : () => this.orderUpdating = false
      })
    },
    createQuotation() {
      this.orderUpdating = true;
      this.$inertia.post(this.route('customer.orders.create-quotation'), {
        orderId: this.order.id
      },{
        onSuccess : () => this.orderUpdating = false
      })
    },

    createCreditInvoice() {
      this.orderUpdating = true;
      this.$inertia.post(this.route('customer.orders.make-credit-invoice'), {
        orderId: this.order.id
      },{
        onSuccess : () => this.orderUpdating = false
      })
    },

    getWarehouseListFor(item_code) {
      let items = [];

      if(this.warehouses[item_code]) {

        this.warehouses[item_code].forEach((item) => {
          items.push({
            id: item.id,
            name: item.name
          })
        });
      }

      return items;
    },

    updateItemPrice(item, evt) {
      let original_price = item.price_total;

      if(item.quantity_approved !== "" || item.quantity_approved !== NaN) {
        item.price_total = item.price * item.quantity_approved;

        let diff = item.price_total - original_price;

        this.total_price = this.total_price + diff;
      }
    },

    setOrderDetails(order) {
      mapFormValue(this.form, order)
      this.form.items = order.items.map(itm => {
        if (itm.quantity_approved === null) {
          itm.quantity_approved = itm.quantity;
        }
        return itm;
      })
      this.form.delivery_location = order.delivery_location || this.customerAccountLocation
    },

    updateOrder() {
     if (this.canAdmin('update-orders') || this.canAdmin('update-order-item-location')) {
       this.form.put(this.route('customer.orders.update', this.order.id))
     }
    },
    approveOrder() {
      if (this.canAdmin('approve-orders')) {
        this.form.put(this.route('customer.orders.update', {customerOrder: this.order.id, approval: true}))
      }
    }
  },

  mounted() {
    this.setOrderDetails(this.order)
    if (['open'].includes(this.order.status)) {
      this.can_update_order_items = true;
      this.can_update_order_items = true;
    } else {
      if (this.$page.props.auth.user.role.super_role) {
        this.can_update_order_items = true;
      } else {
        this.can_update_order_items = false;
        this.can_update_order_items = this.canAdmin('update-order-item-location');
      }
    }
  }
}
</script>
